import axios from "axios";
import { BACKEND_URL } from "../config/config";
import useSessionToken from "../hooks/useSessionToken";
import { UserAccountExtended } from "../types/UserAccount";
import { getResponseError } from "../utils/getResponseError";
import { publish } from "../utils/pubsub";

export class UserApi {
  static path = BACKEND_URL + "api/Profile";

  /**
   *
   * @param id User id. Use "me" to update the currently logged in user.
   * @param data User data to update. Only first name and last name are currently supported.
   */
  static async update(
    id: "me" | string,
    user: UserAccountExtended | undefined,
    sessionToken: string | null
  ) {
    try {
      const config = {
        headers: { Authorization: `Bearer ${sessionToken}` },
      };
      const data = {
        ...user?.userData,
        email: user?.email,
      };
      const r = await axios.put(`${this.path}/${id}`, data, config);
      const result = r.data as Record<string, unknown>;

      publish("user", {
        event: "updated",
        id: id,
      });

      return [true, result] as const;
    } catch (ex) {
      console.warn(ex);
      return [false, getResponseError(ex)] as const;
    }
  }

  /**
   *
   * @param id User id. Use "me" to update the currently logged in user.
   * @param data User data to update. Only first name and last name are currently supported.
   */
  static async delete(id: "me" | string, sessionToken: string | null) {
    try {
      const config = {
        headers: { Authorization: `Bearer ${sessionToken}` },
      };
      const r = await axios.delete(`${this.path}/${id}`, config);
      const result = r.data as Record<string, unknown>;

      publish("user", {
        event: "updated",
        id: id,
      });

      return [true, result] as const;
    } catch (ex) {
      console.warn(ex);
      return [false, getResponseError(ex)] as const;
    }
  }

  /**
   * TODO: Unclear whether this should be in Profile or Users API.
   * Using this as a temporary solution.
   *
   * @param id User id. Use "me" to update the currently logged in user.
   * @param data Image in blob format to update
   */
  static async updateImage(id: "me" | string, image: Blob) {
    try {
      const r = await axios.put(`/api/core/v1/profile/${id}/image`, image, {
        headers: {
          "Content-Type": image.type,
        },
      });
      const result = r.data as Record<string, unknown>;

      publish("user", {
        event: "updated",
        id: id,
      });

      return [true, result] as const;
    } catch (ex) {
      console.warn(ex);
      return [false, getResponseError(ex)] as const;
    }
  }

  /**
   * @param id User id. Use "me" to update the locale of the currently logged in user.
   * @param locale Locale to update. Use null to reset to default.
   */
  static async updateLocale(id: "me" | string, locale: string | null) {
    try {
      const r = await axios.put(`${this.path}/${id}/locale`, {
        locale,
      });
      const result = r.data as { [key: string]: unknown };

      publish("user", {
        event: "updated",
        id,
      });

      return [true, result] as const;
    } catch (ex) {
      console.warn(ex);
      return [false, getResponseError(ex)] as const;
    }
  }

  /**
   * @param id User id. Use "me" to delete the profile image of the currently logged in user.
   */

  static async deleteImage(id: "me" | string) {
    try {
      const r = await axios.delete(`/api/core/v1/profile/${id}/image`);
      const result = r.data as Record<string, unknown>;

      publish("user", {
        event: "updated",
        id: id,
      });

      return [true, result] as const;
    } catch (ex) {
      console.warn(ex);
      return [false, getResponseError(ex)] as const;
    }
  }
}
